import React, { useCallback, useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Tooltip as RangeTooltip, Tooltip } from 'react-tippy';

import {
	ExploreButton,
	Header,
	LoadingContainer,
	NoData,
	SectionWrapper,
	Title,
	TooltipRowTitle,
} from './ChartList.styles';
import {
	ApButtonSecondary,
	ApIcon,
	ApLoaderDefault,
	ApLoaderText,
	colors,
} from '@alixpartners/ui-components';
import { renderToString } from 'react-dom/server';
import {
	ColorSymbol,
	ColorWrapper,
	LegendWrapper,
	TooltipGrid,
	TooltipHeader,
	TooltipRowValue,
	TooltipWrapper,
} from '../../Components/Tooltip.styles';
import CategoryMultiSelect from 'Settings/FirstUsageSettings/CategoryMultiSelect';
import {
	MultiSelectWrapper,
	RangeWrapper,
	SpendWrapper,
} from '../../WidgetSettings.js/WidgetSettings.styles';
import filterNames from 'utils/filterNames';
import { useFilterContext } from 'contexts/FilterContext';
import { useChartContext } from 'contexts/CategoryChartContext';
import ExpandedModal from '../../Components/ExpandedModal';
import Slider from 'rc-slider';
import { IconWrapper, TooltipContent } from '../../CategoryWidget.styles';
import { formatSpendWithDecimalAndSymbol } from 'utils/number';

const StackedBar = ({
	chartTitle,
	isParent,
	data: chartData,
	suppliers,
	categories,
}) => {
	const [isExpandedView, setIsExpandedView] = useState(false);
	const [selectedFilters, setSelectedFilters] = useState([]);
	useEffect(
		() => {
			const topSuppliers = suppliers.slice(0, 3);
			setSelectedFilters(topSuppliers);
		},
		[suppliers]
	);

	const filteredData =
		selectedFilters.length === 0
			? []
			: chartData
					.filter(chart => selectedFilters.includes(chart.name))
					.slice(0, 3);

	const { state: newState, actions } = useFilterContext();
	const initialStackedPlotSettings = {
		[filterNames.stackedBarChartJobClass]: newState.stackedBarChartJobClass,
	};
	const [stackedPlotSettings, setStackedPLotSettings] = useState(
		initialStackedPlotSettings
	);

	const { setMultipleFilters } = actions;
	const [stackedPlotGAmt, setStackedPlotGAmt] = useState(
		newState.stackedBarChartGrossAmountRange
	);

	const { state } = useChartContext();
	const { markupLoading } = state;

	const handleChange = useCallback((evnt, action, chart) => {
		if (action === 'jobClass' && chart === 'stacked') {
			setStackedPLotSettings(prev => ({
				...prev,
				[filterNames.stackedBarChartJobClass]: evnt,
			}));
		}
		if (action === 'supplier' && chart === 'stacked') {
			setSelectedFilters(evnt);
		}
	}, []);
	const infoTooltipContent = (
		<TooltipContent>Select maxiumum of three supplier.</TooltipContent>
	);

	const chartOptions = {
		chart: {
			type: 'column',
			height: isParent ? '20%' : '10%',
			zoomBySingleTouch: true,
			zoomType: 'xy',
		},
		exporting: { enabled: false, allowHTML: true },
		credits: { enabled: false },
		title: { text: '' },

		xAxis: {
			min: 0,
			title: {
				text: '',
			},
			categories: categories,
			tickWidth: 0,
			gridLineWidth: 0,
			lineColor: colors.black,
			lineWidth: 1,
			labels: {
				enabled: false,
				format: '{value}%',
			},
		},
		yAxis: {
			min: 0,
			title: {
				text: '',
			},
			endOnTick: false,
			maxPadding: 0.2,
			stackLabels: {
				enabled: false,
				style: {
					fontWeight: 'bold',
					color: 'gray',
				},
			},
		},

		legend: {
			enabled: false,
		},
		plotOptions: {
			series: {
				stacking: 'normal',
			},
		},
		colors: ['#032E45', '#448EB4', '#45B9F7', '#AFE1FC'],
	};

	return (
		<SectionWrapper>
			<Header>
				<Title>{chartTitle}</Title>
				{isParent && (
					<ExploreButton
						title="Explore"
						className="explore"
						onClick={() => setIsExpandedView(true)}
					>
						<ApIcon title="Explore" material={true} iconName="find_in_page" />
					</ExploreButton>
				)}
			</Header>
			{!isParent && (
				<div style={{ display: 'flex' }}>
					<MultiSelectWrapper>
						<CategoryMultiSelect
							name="Job class"
							label="Job class"
							options={newState.jobClassOptions}
							selected={stackedPlotSettings.stackedBarChartJobClass}
							onChange={e => handleChange(e, 'jobClass', 'stacked')}
						/>
					</MultiSelectWrapper>
					<MultiSelectWrapper>
						<CategoryMultiSelect
							name="Supplier"
							label="Supplier"
							options={suppliers.map(supp => ({
								label: supp,
								value: supp,
							}))}
							selected={selectedFilters}
							onChange={e => handleChange(e, 'supplier', 'stacked')}
						/>
					</MultiSelectWrapper>
					<Tooltip
						position="top"
						animation="fade"
						arrow
						html={infoTooltipContent}
						trigger="mouseenter"
					>
						<IconWrapper>
							<ApIcon
								iconName="baseline_info"
								iconSize={16}
								iconColor={colors.grey6}
							/>
						</IconWrapper>
					</Tooltip>
					<SpendWrapper>
						<span>Gross amount</span>
						<RangeWrapper>
							<RangeTooltip
								position="top"
								animation="fade"
								theme="light"
								arrow
								trigger="mouseenter"
								html={
									<TooltipContent>
										<span>Lower:</span>
										{stackedPlotGAmt[0]}
										{/* <br /> */} &nbsp;
										<span>Upper:</span> {stackedPlotGAmt[1]}
									</TooltipContent>
								}
								popperOptions={{
									modifiers: {
										preventOverflow: {
											enabled: true,
											boundariesElement: 'window',
										},
									},
								}}
							>
								<Slider
									range
									value={stackedPlotGAmt}
									min={newState.grossAmountRange[0]}
									max={newState.grossAmountRange[1]}
									allowCross={false}
									draggableTrack={false}
									onChange={e => {
										setStackedPlotGAmt(e);
										handleChange(e, 'grossamt', 'stacked');
									}}
								/>
							</RangeTooltip>
						</RangeWrapper>
					</SpendWrapper>
					<ApButtonSecondary
						onClick={() => {
							const obj = {};
							for (const key in stackedPlotSettings) {
								if (
									Array.isArray(stackedPlotSettings[key]) &&
									stackedPlotSettings[key].length > 0
								) {
									obj[key] = stackedPlotSettings[key];
								}
							}
							setMultipleFilters({
								...obj,
								[filterNames.stackedBarChartGrossAmountRange]: stackedPlotGAmt,
							});
						}}
						style={{ marginLeft: 'auto' }}
					>
						Save
					</ApButtonSecondary>
				</div>
			)}
			{markupLoading === true ? (
				<LoadingContainer>
					<ApLoaderDefault loaderText={() => <ApLoaderText />} />
				</LoadingContainer>
			) : (
				<>
					{filteredData.length === 0 ? (
						<NoData>No data available</NoData>
					) : (
						<>
							{filteredData[0] && (
								<HighchartsReact
									highcharts={Highcharts}
									options={{
										...chartOptions,
										chart: {
											type: 'column',
											height: isParent ? '17.5%' : '10%',
											marginLeft: 42,
											plotBackgroundColor: colors.grey100,
										},
										plotOptions: {
											series: {
												shadow: false,
												stacking: 'normal',
											},
										},
										series: filteredData[0]?.data,
										tooltip: {
											shared: true,
											useHTML: true,
											backgroundColor: 'rgba(255,255,255, 0.9)',
											borderWidth: 0,
											borderRadius: 2,
											outside: true,
											formatter: function() {
												const { points, x } = this;
												return renderToString(
													<TooltipWrapper>
														<TooltipHeader>
															Markup Category {x}% <br />
															{points[0].series.userOptions.supplier}
														</TooltipHeader>
														<TooltipGrid>
															{points.map(pointData => (
																<React.Fragment key={pointData.point.name}>
																	<ColorWrapper>
																		<ColorSymbol color={pointData.color} />
																	</ColorWrapper>
																	<TooltipRowTitle>
																		{pointData.series.name}
																	</TooltipRowTitle>
																	<TooltipRowValue>
																		{formatSpendWithDecimalAndSymbol(
																			pointData.y,
																			'$',
																			false,
																			1
																		)}
																	</TooltipRowValue>
																</React.Fragment>
															))}
															<React.Fragment>
																<ColorWrapper>
																	<ColorSymbol color={colors.black} />
																</ColorWrapper>
																<TooltipRowTitle>Total</TooltipRowTitle>
																<TooltipRowValue>
																	{formatSpendWithDecimalAndSymbol(
																		points[0].total,
																		'$',
																		false,
																		1
																	)}
																</TooltipRowValue>
															</React.Fragment>
														</TooltipGrid>
													</TooltipWrapper>
												);
											},
										},
									}}
								/>
							)}
							{filteredData[1] && (
								<HighchartsReact
									highcharts={Highcharts}
									options={{
										...chartOptions,
										chart: {
											type: 'column',
											height: isParent ? '17.5%' : '10%',
											plotBackgroundColor: colors.grey300,
											marginLeft: 42,
										},
										plotOptions: {
											series: {
												shadow: false, // Disable shadows for all series
												stacking: 'normal',
											},
										},
										series: filteredData[1].data,
										tooltip: {
											shared: true,
											useHTML: true,
											backgroundColor: 'rgba(255,255,255, 0.9)',
											borderWidth: 0,
											borderRadius: 2,
											outside: true,
											formatter: function() {
												const { points, x } = this;
												return renderToString(
													<TooltipWrapper>
														<TooltipHeader>
															Markup Category {x}% <br />
															{points[0].series.userOptions.supplier}
														</TooltipHeader>
														<TooltipGrid>
															{points.map(pointData => (
																<React.Fragment key={pointData.point.name}>
																	<ColorWrapper>
																		<ColorSymbol color={pointData.color} />
																	</ColorWrapper>
																	<TooltipRowTitle>
																		{pointData.series.name}
																	</TooltipRowTitle>
																	<TooltipRowValue>
																		{formatSpendWithDecimalAndSymbol(
																			pointData.y,
																			'$',
																			false,
																			1
																		)}
																	</TooltipRowValue>
																</React.Fragment>
															))}
															<React.Fragment>
																<ColorWrapper>
																	<ColorSymbol color={colors.black} />
																</ColorWrapper>
																<TooltipRowTitle>Total</TooltipRowTitle>
																<TooltipRowValue>
																	{formatSpendWithDecimalAndSymbol(
																		points[0].total,
																		'$',
																		false,
																		1
																	)}
																</TooltipRowValue>
															</React.Fragment>
														</TooltipGrid>
													</TooltipWrapper>
												);
											},
										},
									}}
								/>
							)}
							{filteredData[2] && (
								<HighchartsReact
									highcharts={Highcharts}
									options={{
										...chartOptions,
										chart: {
											type: 'column',
											height: isParent ? '25%' : '10%',
											marginLeft: 42,
											plotBackgroundColor: colors.grey400,
										},

										xAxis: {
											min: 0,
											title: {
												text: 'MarkUp ',
											},
											categories: categories,
											gridLineWidth: 0,
											lineColor: colors.black,
											lineWidth: 1,
											labels: {
												format: '{value}%',
											},
										},
										plotOptions: {
											series: {
												shadow: false, // Disable shadows for all series
												stacking: 'normal',
											},
										},
										series: filteredData[2]?.data,
										tooltip: {
											shared: true,
											useHTML: true,
											backgroundColor: 'rgba(255,255,255, 0.9)',
											borderWidth: 0,
											borderRadius: 2,
											outside: true,
											formatter: function() {
												const { points, x } = this;
												return renderToString(
													<TooltipWrapper>
														<TooltipHeader>
															Markup Category {x}% <br />
															{points[0].series.userOptions.supplier}
														</TooltipHeader>
														<TooltipGrid>
															{points.map(pointData => (
																<React.Fragment key={pointData.point.name}>
																	<ColorWrapper>
																		<ColorSymbol color={pointData.color} />
																	</ColorWrapper>
																	<TooltipRowTitle>
																		{pointData.series.name}
																	</TooltipRowTitle>
																	<TooltipRowValue>
																		{formatSpendWithDecimalAndSymbol(
																			pointData.y,
																			'$',
																			false,
																			1
																		)}
																	</TooltipRowValue>
																</React.Fragment>
															))}
															<React.Fragment>
																<ColorWrapper>
																	<ColorSymbol color={colors.black} />
																</ColorWrapper>
																<TooltipRowTitle>Total</TooltipRowTitle>
																<TooltipRowValue>
																	{formatSpendWithDecimalAndSymbol(
																		points[0].total,
																		'$',
																		false,
																		1
																	)}
																</TooltipRowValue>
															</React.Fragment>
														</TooltipGrid>
													</TooltipWrapper>
												);
											},
										},
									}}
								/>
							)}
							{!isParent && (
								<LegendWrapper>
									{filteredData[0] && (
										<span>
											<ColorWrapper>
												<ColorSymbol color={colors.grey100} />
											</ColorWrapper>
											<TooltipRowTitle>
												&nbsp;{filteredData[0].name}
											</TooltipRowTitle>
										</span>
									)}
									{filteredData[1] && (
										<span>
											<ColorWrapper>
												<ColorSymbol color={colors.grey300} />
											</ColorWrapper>
											<TooltipRowTitle>
												&nbsp;{filteredData[1].name}
											</TooltipRowTitle>
										</span>
									)}
									{filteredData[2] && (
										<span>
											<ColorWrapper>
												<ColorSymbol color={colors.grey400} />
											</ColorWrapper>
											<TooltipRowTitle>
												&nbsp;{filteredData[2].name}
											</TooltipRowTitle>
										</span>
									)}
								</LegendWrapper>
							)}
						</>
					)}
					{isExpandedView && (
						<ExpandedModal
							heading={chartTitle}
							show={true}
							handleClose={() => setIsExpandedView(state => !state)}
						>
							<StackedBar
								chartheight={'10%'}
								data={chartData}
								categories={categories}
								suppliers={suppliers}
							/>
						</ExpandedModal>
					)}
				</>
			)}
		</SectionWrapper>
	);
};

export default React.memo(StackedBar);
